import YouTubePlayer from 'youtube-player';
import VimeoPlayer from '@vimeo/player';

const VIDEO_WRAPPER_SELECTOR = '.sts-video';
const PLAY_BUTTON_SELECTOR = '.sts-video__play-button';
const FULLSCREEN_BUTTON_SELECTOR = '.sts-video__fullscreen-button';
const MEDIA_SELECTOR = '.sts-video__media';
const EXTERNAL_SHELL_SELECTOR = '.sts-video__external-shell';
const VIDEO_ELEMENT_SELECTOR = 'video.sts-video__player';
const LOADING_SELECTOR = '.sts-video__loading';
const SKELETON_SELECTOR = '.sts-video__skeleton';

type Provider = 'youtube' | 'vimeo' | 'upload' | 'unknown';

const youtubePlayers = new WeakMap<HTMLIFrameElement, ReturnType<typeof YouTubePlayer>>();
const vimeoPlayers = new WeakMap<HTMLIFrameElement, VimeoPlayer>();
const vimeoInitialized = new WeakSet<HTMLIFrameElement>();

const getYouTubePlayer = (iframe: HTMLIFrameElement) => {
    const existing = youtubePlayers.get(iframe);
    if (existing) {
        return existing;
    }
    const player = YouTubePlayer(iframe);
    youtubePlayers.set(iframe, player);
    return player;
};

const getVimeoPlayer = (iframe: HTMLIFrameElement) => {
    const existing = vimeoPlayers.get(iframe);
    if (existing) {
        return existing;
    }
    const player = new VimeoPlayer(iframe);
    vimeoPlayers.set(iframe, player);
    return player;
};

const initVimeoListeners = (iframe: HTMLIFrameElement, onPlayState: (isPlaying: boolean) => void) => {
    if (vimeoInitialized.has(iframe)) {
        return;
    }
    vimeoInitialized.add(iframe);

    const player = getVimeoPlayer(iframe);
    player
        .ready()
        .then(() => {
            player.on('play', () => onPlayState(true));
            player.on('pause', () => onPlayState(false));
            player.on('ended', () => onPlayState(false));
        })
        .catch(() => {});
};

const buildPlayerUrl = (url: string, provider: Provider, { autoplay = false } = {}) => {
    if (!url) {
        return url;
    }

    try {
        const parsed = new URL(url);
        if (provider === 'youtube') {
            parsed.searchParams.set('rel', '0');
            parsed.searchParams.set('controls', '0');
            parsed.searchParams.set('modestbranding', '1');
            parsed.searchParams.set('playsinline', '1');
            parsed.searchParams.set('enablejsapi', '1');
            parsed.searchParams.set('origin', window.location.origin);
            parsed.searchParams.set('autoplay', autoplay ? '1' : '0');
        } else if (provider === 'vimeo') {
            parsed.searchParams.set('title', '0');
            parsed.searchParams.set('byline', '0');
            parsed.searchParams.set('portrait', '0');
            parsed.searchParams.set('api', '1');
            parsed.searchParams.set('autoplay', autoplay ? '1' : '0');
        } else if (autoplay) {
            parsed.searchParams.set('autoplay', '1');
        }
        return parsed.toString();
    } catch (error) {
        return url;
    }
};

const applyRuntimeEmbedOrigin = (
    iframe: HTMLIFrameElement,
    provider: Provider,
    externalShell: HTMLElement | null
) => {
    if (provider !== 'youtube') {
        return;
    }

    const baseUrl = iframe.dataset.embedUrl || iframe.src;

    try {
        const parsed = new URL(baseUrl);
        parsed.searchParams.set('origin', window.location.origin);
        const runtimeUrl = parsed.toString();

        iframe.dataset.embedUrl = runtimeUrl;
        if (externalShell) {
            externalShell.dataset.embedUrl = runtimeUrl;
        }
        if (iframe.src !== runtimeUrl) {
            iframe.src = runtimeUrl;
        }
    } catch {
        // Keep the saved URL unchanged when a third-party URL cannot be parsed.
    }
};

const pauseExternalVideo = (iframe: HTMLIFrameElement | null) => {
    if (!iframe) {
        return;
    }

    const provider = (iframe.dataset.provider || 'unknown') as Provider;
    if (provider === 'youtube') {
        const player = getYouTubePlayer(iframe);
        player.pauseVideo().catch(() => {});
    } else if (provider === 'vimeo') {
        const player = getVimeoPlayer(iframe);
        player.pause().catch(() => {});
    } else {
        const baseUrl = iframe.dataset.embedUrl || iframe.src;
        iframe.src = buildPlayerUrl(baseUrl, provider, { autoplay: false });
    }
};

const playExternalVideo = (iframe: HTMLIFrameElement | null) => {
    if (!iframe) {
        return;
    }

    const provider = (iframe.dataset.provider || 'unknown') as Provider;
    if (provider === 'youtube') {
        const player = getYouTubePlayer(iframe);
        player.playVideo().catch(() => {});
    } else if (provider === 'vimeo') {
        const player = getVimeoPlayer(iframe);
        player.play().catch(() => {});
    } else {
        const baseUrl = iframe.dataset.embedUrl || iframe.src;
        iframe.src = buildPlayerUrl(baseUrl, provider, { autoplay: true });
    }
};

const getPlayingClassTarget = (wrapper: HTMLElement) =>
    wrapper.closest<HTMLElement>('.sts-block-video') ?? wrapper;

const createSkeletonElement = (): HTMLDivElement => {
    const skeleton = document.createElement('div');
    skeleton.className = 'sts-video__skeleton';
    skeleton.setAttribute('aria-hidden', 'true');
    return skeleton;
};

const initVideoPlayer = (wrapper: HTMLElement) => {
    const playButton = wrapper.querySelector<HTMLButtonElement>(PLAY_BUTTON_SELECTOR);
    const fullscreenButton = wrapper.querySelector<HTMLButtonElement>(FULLSCREEN_BUTTON_SELECTOR);
    const media = wrapper.querySelector<HTMLElement>(MEDIA_SELECTOR);

    if (!playButton || !media) {
        return;
    }

    if (playButton.dataset.videoScriptInitialized === 'true') {
        return;
    }
    playButton.dataset.videoScriptInitialized = 'true';

    const sourceType = media.dataset.videoSource as Provider | undefined;
    const videoElement = media.querySelector<HTMLVideoElement>(VIDEO_ELEMENT_SELECTOR);
    const externalShell = media.querySelector<HTMLElement>(EXTERNAL_SHELL_SELECTOR);
    const iframe = externalShell?.querySelector<HTMLIFrameElement>('.sts-video__iframe') ?? null;
    const loadingOverlay = media.querySelector<HTMLElement>(LOADING_SELECTOR);
    let skeleton = media.querySelector<HTMLElement>(SKELETON_SELECTOR);
    
    // Create skeleton element if it doesn't exist
    if (!skeleton) {
        skeleton = createSkeletonElement();
        media.appendChild(skeleton);
    }
    
    const classTarget = getPlayingClassTarget(wrapper);

    const hideLoadingOverlay = () => {
        loadingOverlay?.classList.remove('is-visible');
    };

    const showLoadingOverlay = () => {
        loadingOverlay?.classList.add('is-visible');
    };

    const hideSkeleton = () => {
        skeleton?.classList.add('sts-video__skeleton--hidden');
    };

    const showSkeleton = () => {
        skeleton?.classList.remove('sts-video__skeleton--hidden');
    };

    const setPlayingState = (isPlaying: boolean) => {
        playButton.dataset.playing = isPlaying ? 'true' : 'false';
        playButton.setAttribute('aria-pressed', String(Boolean(isPlaying)));
        if (isPlaying) {
            classTarget.classList.add('is-playing');
        } else {
            classTarget.classList.remove('is-playing');
        }
        if (videoElement && sourceType === 'upload') {
            videoElement.controls = isPlaying;
        }
    };

    setPlayingState(false);

    const handleUploadToggle = () => {
        if (!videoElement) {
            return;
        }

        if (videoElement.paused) {
            videoElement
                .play()
                .then(() => setPlayingState(true))
                .catch(() => setPlayingState(false));
        } else {
            videoElement.pause();
            setPlayingState(false);
        }
    };

    if (videoElement) {
        if (skeleton) {
            showSkeleton();
            const readyEvents = ['loadeddata', 'canplay', 'canplaythrough'] as const;
            readyEvents.forEach((eventName) => {
                videoElement.addEventListener(eventName, hideSkeleton, { once: true });
            });
        }

        if (loadingOverlay) {
            const loadingEvents = ['waiting', 'stalled'] as const;
            const readyEvents = ['loadeddata', 'canplay', 'canplaythrough', 'playing'] as const;
            loadingEvents.forEach((eventName) => {
                videoElement.addEventListener(eventName, showLoadingOverlay);
            });
            readyEvents.forEach((eventName) => {
                videoElement.addEventListener(eventName, hideLoadingOverlay);
            });
        }

        videoElement.addEventListener('play', () => setPlayingState(true));
        videoElement.addEventListener('pause', () => setPlayingState(false));
        videoElement.addEventListener('ended', () => setPlayingState(false));
    }

    const handleExternalToggle = () => {
        if (!iframe) {
            return;
        }

        const isPlaying = playButton.dataset.playing === 'true';
        if (isPlaying) {
            pauseExternalVideo(iframe);
            setPlayingState(false);
            return;
        }

        playExternalVideo(iframe);
        setPlayingState(true);
    };

    const attachExternalListeners = (targetIframe: HTMLIFrameElement) => {
        if (targetIframe.dataset.externalListenersInitialized === 'true') {
            return;
        }
        targetIframe.dataset.externalListenersInitialized = 'true';

        const provider = (targetIframe.dataset.provider || sourceType || 'unknown') as Provider;
        applyRuntimeEmbedOrigin(targetIframe, provider, externalShell);
        if (provider === 'youtube') {
            const player = getYouTubePlayer(targetIframe);
            player.on('stateChange', (event: { data?: number }) => {
                if (event?.data === 1) {
                    setPlayingState(true);
                } else if (event?.data === 2 || event?.data === 0) {
                    setPlayingState(false);
                }
            });
            return;
        }

        if (provider === 'vimeo') {
            initVimeoListeners(targetIframe, setPlayingState);
            targetIframe.addEventListener('load', () => {
                initVimeoListeners(targetIframe, setPlayingState);
            });
        }
    };

    if (iframe) {
        attachExternalListeners(iframe);
    } else if (externalShell) {
        const observer = new MutationObserver(() => {
            const nextIframe = externalShell.querySelector<HTMLIFrameElement>('.sts-video__iframe');
            if (!nextIframe) {
                return;
            }

            observer.disconnect();
            attachExternalListeners(nextIframe);
        });

        observer.observe(externalShell, { childList: true, subtree: true });
    }

    // Handle iframe skeleton loading
    if (iframe && skeleton) {
        showSkeleton();
        
        // Hide skeleton when iframe loads
        iframe.addEventListener('load', () => {
            hideSkeleton();
        }, { once: true });

        // Fallback: hide skeleton after a timeout if load event doesn't fire
        const skeletonTimeout = setTimeout(() => {
            hideSkeleton();
        }, 3000);

        iframe.addEventListener('load', () => {
            clearTimeout(skeletonTimeout);
        }, { once: true });
    }

    playButton.addEventListener('click', (event) => {
        event.preventDefault();
        if (sourceType === 'upload') {
            handleUploadToggle();
            return;
        }

        handleExternalToggle();
    });

    if (fullscreenButton && sourceType === 'upload') {
        const canFullscreen = Boolean(
            wrapper.requestFullscreen && document.fullscreenEnabled && document.exitFullscreen
        );

        if (!canFullscreen) {
            fullscreenButton.disabled = true;
            return;
        }

        const enterLabel = fullscreenButton.dataset.enterLabel || '';
        const exitLabel = fullscreenButton.dataset.exitLabel || '';

        const setFullscreenState = (isFullscreen: boolean) => {
            fullscreenButton.dataset.fullscreen = isFullscreen ? 'true' : 'false';
            fullscreenButton.setAttribute('aria-pressed', String(Boolean(isFullscreen)));
            if (enterLabel || exitLabel) {
                fullscreenButton.setAttribute('aria-label', isFullscreen ? exitLabel : enterLabel);
            }
        };

        const handleFullscreenChange = () => {
            setFullscreenState(document.fullscreenElement === wrapper);
        };

        setFullscreenState(false);
        document.addEventListener('fullscreenchange', handleFullscreenChange);

        fullscreenButton.addEventListener('click', (event) => {
            event.preventDefault();
            if (document.fullscreenElement) {
                document.exitFullscreen?.();
                return;
            }

            wrapper.requestFullscreen?.();
        });
    }
};

export const initVideoPlayers = (root: ParentNode | Document | null = document) => {
    if (!root) {
        return;
    }

    const wrappers = root.querySelectorAll<HTMLElement>(VIDEO_WRAPPER_SELECTOR);
    wrappers.forEach((wrapper) => initVideoPlayer(wrapper));
};
